import { useMemo, useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { MoreVertical, Plus, Pencil, ShieldCheck, Search } from "lucide-react";

import { apiFetch } from "@/lib/api";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { PermissionsDialog } from "./dialog/PermissionsDialog";
import { UserMenu } from "@/components/Holizontal/UserMenu";


type Role = {
  id: number;
  name: string;
  description?: string | null;
};

type CursorMeta = {
  per_page: number;
  next_cursor: string | null;
  prev_cursor: string | null;
  has_more: boolean;
};

type CursorResponse = {
  data: Role[];
  meta: CursorMeta;
  anchor_id?: number | null;
};

function clsx(...s: Array<string | false | undefined | null>) {
  return s.filter(Boolean).join(" ");
}

// ✅ endpoints (adjust if different)
const ENDPOINTS = {
  list: "/users/roles",
  create: "/users/roles",
  update: (id: number) => `/users/roles/${id}`,
};

async function fetchRolesCursor({
  cursor,
  q,
  perPage,
}: {
  cursor: string | null;
  q: string;
  perPage: number;
}): Promise<CursorResponse> {
  const params = new URLSearchParams();
  params.set("per_page", String(perPage));
  if (cursor) params.set("cursor", cursor);
  if (q?.trim()) params.set("q", q.trim());

  // expected API shape: { data, meta, anchor_id }
  const res = await apiFetch<CursorResponse>(`${ENDPOINTS.list}?${params.toString()}`);
  return res;
}

function useNearBottom(ref: React.RefObject<HTMLElement>, thresholdPx = 420) {
  const [nearBottom, setNearBottom] = useState(false);

  useEffect(() => {
    const el = ref.current;

    const onScroll = () => {
      const target = el ?? document.documentElement;
      const scrollTop = el ? el.scrollTop : window.scrollY;
      const height = el ? el.clientHeight : window.innerHeight;
      const scrollHeight = el ? el.scrollHeight : document.documentElement.scrollHeight;

      setNearBottom(scrollTop + height >= scrollHeight - thresholdPx);
    };

    // initial
    onScroll();

    if (el) {
      el.addEventListener("scroll", onScroll, { passive: true });
    } else {
      window.addEventListener("scroll", onScroll, { passive: true });
    }

    window.addEventListener("resize", onScroll);

    return () => {
      if (el) el.removeEventListener("scroll", onScroll);
      else window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [ref, thresholdPx]);

  return nearBottom;
}

export default function RolesPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const dir =
    (document.documentElement.getAttribute("dir") as "ltr" | "rtl") || "ltr";
  const isRTL = dir === "rtl";

  // If your Layout main is the scroll container, use it.
  // Otherwise it will fall back to window scroll.
  const mainEl = document.querySelector("main");
  const scrollRef = useRef<HTMLElement | null>((mainEl as HTMLElement) || null);

  // UI state
  const [q, setQ] = useState("");
  const [perPage, setPerPage] = useState(10);

  // modals
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState<{ id?: number; name: string; description: string }>({
    name: "",
    description: "",
  });

  // ✅ Infinite Query with cursor
  const rolesInf = useInfiniteQuery({
    queryKey: ["roles_cursor", q, perPage],
    queryFn: ({ pageParam }) =>
      fetchRolesCursor({
        cursor: (pageParam as string | null) ?? null,
        q,
        perPage,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => (lastPage?.meta?.has_more ? lastPage?.meta?.next_cursor : null),
    refetchOnWindowFocus: false,
    staleTime: 10_000,
  });

  const pages = rolesInf.data?.pages ?? [];
  const roles = useMemo(() => pages.flatMap((p) => p.data || []), [pages]);
  const meta = pages.length ? pages[pages.length - 1]?.meta : undefined;

  const loadingFirst = rolesInf.isLoading;
  const loadingMore = rolesInf.isFetchingNextPage;

  // ✅ Auto-load when near bottom
  const nearBottom = useNearBottom(scrollRef as any, 420);

  const [permOpen, setPermOpen] = useState(false);
const [permRole, setPermRole] = useState<{ id: number; name: string } | null>(null);

  useEffect(() => {
    if (!nearBottom) return;
    if (rolesInf.hasNextPage && !rolesInf.isFetchingNextPage) {
      rolesInf.fetchNextPage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nearBottom]);

  // ✅ Create mutation (optional, keeps your modals)
  const createMut = useMutation({
    mutationFn: async (payload: { name: string; description?: string }) => {
    return apiFetch(ENDPOINTS.create, {
  method: "POST",
  body: JSON.stringify(payload),
  headers: {
    "Content-Type": "application/json",
  },
});
    },
    onSuccess: async () => {
      toast.success("Role created");
      setCreateOpen(false);
      // reset list (fresh from first cursor)
      await qc.invalidateQueries({ queryKey: ["roles_cursor"] });
    },
    onError: (e: any) => toast.error(e?.message || "Failed to create role"),
  });

  // ✅ Update mutation (optional)
  const updateMut = useMutation({
    mutationFn: async (payload: { id: number; name: string; description?: string }) => {
      return apiFetch<any>(ENDPOINTS.update(payload.id), {
  method: "POST",
  headers: {
    "Content-Type": "application/json",
  },
  body: JSON.stringify({
    name: payload.name,
    description: payload.description,
  }),
});
    },
    onSuccess: async () => {
      toast.success("Role updated");
      setEditOpen(false);
      await qc.invalidateQueries({ queryKey: ["roles_cursor"] });
    },
    onError: (e: any) => toast.error(e?.message || "Failed to update role"),
  });

  const openCreate = () => {
    setForm({ name: "", description: "" });
    setCreateOpen(true);
  };

  const openEdit = (role: Role) => {
    setForm({ id: role.id, name: role.name, description: role.description || "" });
    setEditOpen(true);
  };

  const submitCreate = () => {
    const name = form.name.trim();
    if (!name) return toast.error("Role name is required");
    createMut.mutate({ name, description: form.description?.trim() || "" });
  };

  const submitEdit = () => {
    if (!form.id) return;
    const name = form.name.trim();
    if (!name) return toast.error("Role name is required");
    updateMut.mutate({ id: form.id, name, description: form.description?.trim() || "" });
  };

 

  return (
    <div dir={dir} className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <motion.h1
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-2xl font-bold text-foreground"
        >
          Roles
        </motion.h1>

        <Button
          onClick={openCreate}
          className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white rounded-xl shadow-[0_10px_30px_rgba(124,58,237,0.18)]"
          disabled={loadingFirst}
        >
          <Plus className={clsx("h-4 w-4", isRTL ? "ml-2" : "mr-2")} />
          Create
        </Button>
      </div>

      {/* Error */}
      {rolesInf.isError ? (
        <Card className="shadow-card">
          <CardContent className="p-4 text-sm text-red-600">
            {(rolesInf.error as any)?.message || "Failed to load roles"}
          </CardContent>
        </Card>
      ) : null}

        <UserMenu/>

      <Card className="shadow-card overflow-hidden">
        

          

        <CardContent className="p-0">
          <div className="w-full overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/30">
                  <TableHead className="w-[80px]">#</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className={clsx("w-[140px]", isRTL ? "text-left" : "text-right")}>
                    Action
                  </TableHead>
                </TableRow>
              </TableHeader>

              <TableBody>
                {loadingFirst ? (
                  Array.from({ length: 8 }).map((_, i) => (
                    <TableRow key={`sk-${i}`}>
                      <TableCell className="py-4 text-muted-foreground">—</TableCell>
                      <TableCell className="py-4">
                        <div className="h-4 w-40 rounded bg-muted animate-pulse" />
                      </TableCell>
                      <TableCell className="py-4">
                        <div className="h-4 w-64 rounded bg-muted animate-pulse" />
                      </TableCell>
                      <TableCell className={clsx("py-4", isRTL ? "text-left" : "text-right")}>
                        <div className="inline-block h-8 w-10 rounded bg-muted animate-pulse" />
                      </TableCell>
                    </TableRow>
                  ))
                ) : roles.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="py-10 text-center text-muted-foreground">
                      No data available
                    </TableCell>
                  </TableRow>
                ) : (
                  roles.map((role, idx) => (
                    <TableRow key={role.id} className="hover:bg-muted/20">
                      <TableCell className="text-muted-foreground">{idx + 1}</TableCell>
                      <TableCell className="font-semibold">{role.name}</TableCell>
                      <TableCell className="text-muted-foreground">{role.description || "—"}</TableCell>

                      <TableCell className={clsx(isRTL ? "text-left" : "text-right")}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" className="rounded-xl">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>

                          <DropdownMenuContent align={isRTL ? "start" : "end"} className="rounded-xl">
                            <DropdownMenuItem onClick={() => openEdit(role)}>
                              <Pencil className={clsx("h-4 w-4", isRTL ? "ml-2" : "mr-2")} />
                              Edit
                            </DropdownMenuItem>

                            
                            <DropdownMenuItem
  onClick={() => {
    setPermRole({ id: role.id, name: role.name });
    setPermOpen(true);
  }}
></DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}

                {/* Loading more rows shimmer */}
                {loadingMore
                  ? Array.from({ length: 4 }).map((_, i) => (
                      <TableRow key={`more-${i}`}>
                        <TableCell className="py-4 text-muted-foreground">—</TableCell>
                        <TableCell className="py-4">
                          <div className="h-4 w-40 rounded bg-muted animate-pulse" />
                        </TableCell>
                        <TableCell className="py-4">
                          <div className="h-4 w-64 rounded bg-muted animate-pulse" />
                        </TableCell>
                        <TableCell className={clsx("py-4", isRTL ? "text-left" : "text-right")}>
                          <div className="inline-block h-8 w-10 rounded bg-muted animate-pulse" />
                        </TableCell>
                      </TableRow>
                    ))
                  : null}
              </TableBody>
            </Table>
          </div>

          {/* Footer */}
          <div className="flex flex-col gap-2 border-t border-border/60 p-4 md:flex-row md:items-center md:justify-between">
            <div className="text-sm text-muted-foreground">
              Loaded <span className="font-medium text-foreground">{roles.length}</span>{" "}
              {meta?.has_more ? " (more available)" : " (end)"}
            </div>

            <Button
              variant="outline"
              className="rounded-xl"
              onClick={() => rolesInf.fetchNextPage()}
              disabled={!rolesInf.hasNextPage || rolesInf.isFetchingNextPage}
            >
              {rolesInf.isFetchingNextPage ? "Loading..." : rolesInf.hasNextPage ? "Load more" : "No more"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Create Modal */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle>Create Role</DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <label className="text-sm text-muted-foreground">Role</label>
              <Input
                value={form.name}
                onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))}
                placeholder="Role name"
                className="rounded-xl"
              />
            </div>

            <div>
              <label className="text-sm text-muted-foreground">Description</label>
              <textarea
                value={form.description}
                onChange={(e) => setForm((s) => ({ ...s, description: e.target.value }))}
                placeholder="Description"
                className="w-full min-h-[110px] rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#7C3AED]/40"
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-xl" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={submitCreate}
              className="rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white"
              disabled={createMut.isPending}
            >
              {createMut.isPending ? "Saving..." : "Submit"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle>Edit Role</DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <label className="text-sm text-muted-foreground">Role</label>
              <Input
                value={form.name}
                onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))}
                placeholder="Role name"
                className="rounded-xl"
              />
            </div>

            <div>
              <label className="text-sm text-muted-foreground">Description</label>
              <textarea
                value={form.description}
                onChange={(e) => setForm((s) => ({ ...s, description: e.target.value }))}
                placeholder="Description"
                className="w-full min-h-[110px] rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#7C3AED]/40"
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-xl" onClick={() => setEditOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={submitEdit}
              className="rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white"
              disabled={updateMut.isPending}
            >
              {updateMut.isPending ? "Saving..." : "Submit"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <PermissionsDialog
  open={permOpen}
  onOpenChange={setPermOpen}
  roleId={permRole?.id ?? null}
  roleName={permRole?.name ?? ""}
/>
    </div>
  );
}